// Installed from shadcn.io: https://www.shadcn.io/components/media/tags and modified to fit our needs

import { XIcon } from "lucide-react";
import {
  type ComponentProps,
  createContext,
  type MouseEventHandler,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/atoms/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/atoms/popover";
import { cn } from "~/lib/utils";

type TagsContextType = {
  value?: string;
  setValue?: (value: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width?: number;
  setWidth?: (width: number) => void;
  selectLabel: string;
};

const TagsContext = createContext<TagsContextType | undefined>(undefined);

const useTagsContext = () => {
  const context = useContext(TagsContext);

  if (!context) {
    throw new Error("useTagsContext must be used within a TagsProvider");
  }

  return context;
};

type TagsProps = {
  value?: string;
  setValue?: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  selectLabel: string;
  children?: ReactNode;
  className?: string;
};

export const Tags = ({
  value,
  setValue,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  selectLabel,
  children,
  className,
}: TagsProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [width, setWidth] = useState<number>();
  const ref = useRef<HTMLDivElement>(null);

  const open = controlledOpen ?? uncontrolledOpen;
  const onOpenChange = controlledOnOpenChange ?? setUncontrolledOpen;

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0 && entries[0]) {
        setWidth(entries[0].contentRect.width);
      }
    });

    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <TagsContext.Provider
      value={{
        value,
        setValue,
        open,
        onOpenChange,
        width,
        setWidth,
        selectLabel,
      }}
    >
      <Popover onOpenChange={onOpenChange} open={open}>
        <div className={cn("relative w-full", className)} ref={ref}>
          {children}
        </div>
      </Popover>
    </TagsContext.Provider>
  );
};

export const TagsTrigger = ({
  className,
  children,
  ...props
}: ComponentProps<typeof Button>) => {
  const { selectLabel } = useTagsContext();

  return (
    <PopoverTrigger asChild>
      <Button
        className={cn("h-auto w-full justify-between p-2", className)}
        role="combobox"
        variant="outline"
        {...props}
      >
        <div className="flex flex-wrap items-center gap-1">
          {children}
          <span className="text-muted-foreground px-2 py-px">
            {selectLabel}
          </span>
        </div>
      </Button>
    </PopoverTrigger>
  );
};
type TagsValueProps = {
  className?: string;
  children?: ReactNode;
  onRemove?: () => void;
};

export const TagsValue = ({
  className,
  children,
  onRemove,
}: TagsValueProps) => {
  const handleRemove: MouseEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove?.();
  };

  return (
    <Badge className={className} variant="tags">
      {children}
      {onRemove && (
        <div
          className="hover:text-muted-foreground size-auto cursor-pointer"
          onClick={handleRemove}
        >
          <XIcon size={12} />
        </div>
      )}
    </Badge>
  );
};

export const TagsContent = ({
  className,
  children,
  ...props
}: ComponentProps<typeof PopoverContent>) => {
  const { width } = useTagsContext();

  return (
    <PopoverContent
      className={cn("p-0", className)}
      style={{ width }}
      {...props}
    >
      <Command shouldFilter={false}>{children}</Command>
    </PopoverContent>
  );
};

export const TagsInput = ({
  className,
  ...props
}: ComponentProps<typeof CommandInput>) => (
  <CommandInput className={cn("h-9", className)} {...props} />
);

export const TagsList = ({
  className,
  ...props
}: ComponentProps<typeof CommandList>) => (
  <CommandList className={cn("max-h-[200px]", className)} {...props} />
);

export const TagsEmpty = ({
  children,
  ...props
}: ComponentProps<typeof CommandEmpty>) => (
  <CommandEmpty {...props}>{children ?? "No tags found."}</CommandEmpty>
);

export const TagsGroup = CommandGroup;

export const TagsItem = ({ ...props }: ComponentProps<typeof CommandItem>) => (
  <CommandItem
    className="cursor-pointer items-center justify-between"
    {...props}
  />
);
